import { useMemo, useState, type FunctionComponent } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, message, Popconfirm, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { productsSettingsActions } from "@fitness-app/app-store";
import { IntegrationClient, type ICalendlyIntegration } from "@fitness-app/data-models/entities/ProductsSettings";

import GenericIntegrationForm from "~/components/GenericIntegrationForm/GenericIntegrationForm";
import ModalForm from "~/components/ModalForm/ModalForm";
import { useLoading } from "~/hooks/useLoading";
import { useAppDispatch } from "~/store/initializeStore";

interface OwnProps {
  integration: null | ICalendlyIntegration[];
}

export interface CalendlyIntegrationModel {
  name: string;
  apiToken: string;
  webhookUrl?: string;
  listenEvents?: string[];
  organizationId?: string;
}

type Props = OwnProps;

const ADD_NEW_ITEM = "ADD_NEW_ITEM";

const CALENDLY_EVENTS = [
  { label: "Wydarzenie zostało zaplanowane (Invitee Created)", value: "invitee.created" },
  { label: "Wydarzenie zostało odwołane (Invitee Canceled)", value: "invitee.canceled" },
  { label: "Klient nie przyszedł na wydarzenie (Invitee No Show)", value: "invitee_no_show.created" },
  { label: "Usunięto informacje o braku przyjścia (Invitee No Show Deleted)", value: "invitee_no_show.deleted" },
];

const CalendlyIntegration: FunctionComponent<Props> = ({ integration }) => {
  const { t } = useTranslation(["settings", "common", "dashboard"]);
  const [loading, startLoading, stopLoading] = useLoading();
  const [showModal, toggleModal] = useState(false);
  const dispatch = useAppDispatch();

  const [selectAccountId, changeSelectedAccount] = useState<undefined | string>(undefined);
  const [model, setModel] = useState<CalendlyIntegrationModel | undefined>(undefined);

  const selectedAccount = useMemo(() => {
    return integration?.find((account) => account.id === selectAccountId);
  }, [selectAccountId, integration]);

  const handleFormSubmit = async (data: CalendlyIntegrationModel & { id?: string }) => {
    startLoading();
    const id = data.id || uuid();
    await dispatch(
      productsSettingsActions.integrateWithExternalApi({
        payload: {
          data: {
            ...data,
            id,
            listenEvents: data.listenEvents || [],
          },
          client: IntegrationClient.Calendly,
        },
        onSuccess: () => {
          message.success(
            t<string>(
              selectedAccount
                ? "additionalIntegrations.calendly.editSuccess"
                : "additionalIntegrations.calendly.success",
            ),
          );
          toggleModal(false);
          handleChange(id);
          setModel(undefined);
        },
        onFailure: (errorCode) => {
          if (errorCode === 401) {
            message.error(t<string>("additionalIntegrations.calendly.accessError"));
            return;
          }
          message.error(t<string>("additionalIntegrations.calendly.error"));
        },
      }),
    );
    stopLoading();
  };

  const handleChange = (value?: string | undefined) => {
    if (value === ADD_NEW_ITEM || !value) {
      changeSelectedAccount(undefined);
      toggleModal(true);
      return;
    }
    changeSelectedAccount(value);
  };

  const handleRemoveIntegration = async () => {
    if (!selectedAccount) {
      return;
    }
    message.loading(t<string>("messages.loading.removeAccount"));
    await dispatch(
      productsSettingsActions.removeIntegrationWithExternalApi({
        payload: { id: selectedAccount.id, client: IntegrationClient.Calendly },
        onFailure: () => {
          message.destroy();
          message.error(t<string>("additionalIntegrations.calendly.removeError"));
        },
        onSuccess: () => {
          message.destroy();
          message.info(t<string>("additionalIntegrations.calendly.removeSuccess"));
        },
      }),
    );
  };

  return (
    <Space direction="vertical" size={24}>
      <Typography.Title level={4}>{t<string>("additionalIntegrations.calendly.connectedAccounts")}</Typography.Title>
      <Select<string>
        size="large"
        placeholder={t<string>("selectAccount")}
        style={{ minWidth: 400 }}
        notFoundContent={false}
        onChange={handleChange}
        onSelect={handleChange}
        value={selectedAccount?.id}
        optionFilterProp="title"
        optionLabelProp="title"
      >
        {integration?.map((account) => (
          <Select.Option value={account.id} title={account.name} key={account.name}>
            {account.name}
          </Select.Option>
        ))}
        {
          <Select.Option value={ADD_NEW_ITEM} title={t<string>("addNewAccount")}>
            <Button
              type="link"
              style={{
                flex: "none",
                padding: "8px",
                display: "block",
                cursor: "pointer",
              }}
              className="space-x-2"
            >
              <PlusOutlined /> {t<string>("addNewAccount")}
            </Button>
          </Select.Option>
        }
      </Select>
      {selectedAccount && (
        <>
          <Space direction="horizontal" align="end" style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              type="dashed"
              disabled
              icon={<EditOutlined />}
              onClick={() => {
                setModel({
                  name: selectedAccount.name,
                  apiToken: "",
                  webhookUrl: selectedAccount.webhookUrl || undefined,
                  listenEvents: selectedAccount.listenEvents,
                });
                toggleModal(true);
              }}
            >
              {t<string>("common:button.edit")}
            </Button>
            <Popconfirm
              onConfirm={handleRemoveIntegration}
              title={t<string>("emailClients.deleteWarning")}
              okText={t<string>("common:button.delete")}
              okButtonProps={{ danger: true }}
            >
              <Button type="dashed" danger icon={<DeleteOutlined />}>
                {t<string>("common:button.delete")}
              </Button>
            </Popconfirm>
          </Space>
          <div>
            <Typography.Paragraph strong>{t<string>("additionalIntegrations.calendly.events")}</Typography.Paragraph>
            {selectedAccount.listenEvents?.length ? (
              <ul>
                {selectedAccount.listenEvents.map((event) => (
                  <li key={event}>{CALENDLY_EVENTS.find((e) => e.value === event)?.label || event}</li>
                ))}
              </ul>
            ) : (
              <Typography.Text type="secondary">
                {t<string>("additionalIntegrations.calendly.noEventsSelected")}
              </Typography.Text>
            )}
          </div>
        </>
      )}

      <ModalForm
        visible={showModal}
        loading={loading}
        onCancel={() => toggleModal(false)}
        title={selectedAccount ? t<string>("integrations.editAccount") : t<string>("integrations.addNewAccount")}
      >
        <GenericIntegrationForm<CalendlyIntegrationModel>
          fields={{
            name: {
              label: t<string>("additionalIntegrations.calendly.accountName"),
              rules: [
                {
                  required: true,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ],
              placeholder: t<string>("additionalIntegrations.calendly.accountNamePlaceholder"),
              autofocus: !selectedAccount,
              type: "text",
            },
            organizationId: {
              label: t<string>("additionalIntegrations.calendly.organizationId"),
              rules: [
                {
                  required: true,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ],
              placeholder: t<string>("additionalIntegrations.calendly.organizationIdPlaceholder"),
              type: "text",
            },
            apiToken: {
              label: t<string>("additionalIntegrations.calendly.apiToken"),
              rules: [
                {
                  required: true,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ],
              type: "text",
            },
            webhookUrl: {
              label: t<string>("additionalIntegrations.calendly.webhookUrl"),
              rules: [
                {
                  type: "url",
                  message: t<string>("common:validationErrors.invalidUrl"),
                },
              ],
              placeholder: "https://your-webhook-url.com/calendly-events",
              type: "text",
            },
            listenEvents: {
              type: "custom",
            },
          }}
          onSubmit={handleFormSubmit}
          model={model}
        >
          <Form.Item name="listenEvents" label={t<string>("additionalIntegrations.calendly.events")}>
            <Select
              options={CALENDLY_EVENTS}
              mode="multiple"
              placeholder={t<string>("additionalIntegrations.calendly.eventsPlaceholder")}
            />
          </Form.Item>
        </GenericIntegrationForm>
      </ModalForm>
    </Space>
  );
};

export default CalendlyIntegration;
